import { useCallback, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";

import {
  Bell,
  Boxes,
  CreditCard,
  FileText,
  Loader2,
  Server,
  Settings2,
  ShieldCheck,
  Users,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { OsSupportPolicyEditor } from "@/features/servers/components/software-inventory-report/OsSupportPolicyEditor";
import { TargetVersionsEditor } from "@/features/servers/components/software-inventory-report/TargetVersionsEditor";
import { useInfraSettings } from "@/features/servers/hooks/useInfraSettings";
import { useSoftwareTypes } from "@/features/servers/hooks/useSoftwareTypes";
import {
  DEFAULT_OS_SUPPORT_POLICY,
  DEFAULT_PLATFORM_NOTIFICATION,
  DEFAULT_REVISION_INTERVAL_MONTHS,
} from "@/features/servers/schemas/servers";
import { MedicalTemplatesEditor } from "@/features/medicalTemplates";
import { Can } from "@/features/rbac/components/Can";
import { RolesEditor } from "@/features/rbac/components/RolesEditor";
import { StaffEditor } from "@/features/staff/components/StaffEditor";
import { PlansEditor } from "@/features/plans";
import { ApiEndpointsCatalogEditor } from "@/features/settings/components/ApiEndpointsCatalogEditor";
import { BlacklistEmailEditor } from "@/features/settings/components/BlacklistEmailEditor";
import { PlatformNotificationEditor } from "@/features/settings/components/PlatformNotificationEditor";
import { RevisionIntervalEditor } from "@/features/settings/components/RevisionIntervalEditor";
import { cn } from "@/shared/lib/utils";

const PLATFORM_TABS = [
  "infra",
  "platform",
  "servicios",
  "plantillas",
  "planes",
  "roles",
  "equipo",
] as const;
type PlatformTab = (typeof PLATFORM_TABS)[number];

/** Alias legacy de URL (`?tab=api-keys` → servicios). */
const TAB_ALIASES: Record<string, PlatformTab> = {
  "api-keys": "servicios",
};

/** Mismo underline que inventario (`SoftwareInventoryReport`). */
const TAB_TRIGGER_CLASS = cn(
  "cursor-pointer gap-1.5 rounded-none border-0 border-b-2 border-transparent px-3 py-2.5",
  "bg-transparent shadow-none text-muted-foreground",
  "data-[state=active]:bg-transparent data-[state=active]:shadow-none",
  "dark:data-[state=active]:bg-transparent dark:data-[state=active]:border-b-primary",
  "data-[state=active]:border-b-primary data-[state=active]:text-foreground"
);

function parsePlatformTab(value: string | null): PlatformTab {
  if (!value) return "infra";
  if ((PLATFORM_TABS as readonly string[]).includes(value)) {
    return value as PlatformTab;
  }
  return TAB_ALIASES[value] ?? "infra";
}

/**
 * Configuración de plataforma (viewmed_config + catálogo de servicios).
 * Secciones en tabs para evitar scroll largo (densidad dashboard).
 */
export function PlatformConfig() {
  const { data: settings, isLoading, error } = useInfraSettings();
  const { data: softwareTypes = [] } = useSoftwareTypes();
  const [searchParams, setSearchParams] = useSearchParams();

  const activeTab = useMemo(
    () => parsePlatformTab(searchParams.get("tab")),
    [searchParams]
  );

  const handleTabChange = useCallback(
    (value: string) => {
      const next = parsePlatformTab(value);
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          if (next === "infra") {
            params.delete("tab");
          } else {
            params.set("tab", next);
          }
          return params;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  if (isLoading) {
    return (
      <div className="flex justify-center py-16" role="status" aria-live="polite">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        <span className="sr-only">Cargando configuración</span>
      </div>
    );
  }

  if (error) {
    return (
      <p className="text-sm text-destructive" role="alert">
        {error instanceof Error
          ? error.message
          : "Error al cargar viewmed_config"}
      </p>
    );
  }

  const intervalMonths =
    settings?.revisionIntervalMonths ?? DEFAULT_REVISION_INTERVAL_MONTHS;
  const targetVersions = settings?.softwareTargetVersions ?? {};
  const osPolicy = settings?.osSupportPolicy ?? DEFAULT_OS_SUPPORT_POLICY;
  const blackListEmail = settings?.blackListEmail ?? [];
  const notification = settings?.notification ?? DEFAULT_PLATFORM_NOTIFICATION;

  return (
    <div className="space-y-5">
      <header className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0 space-y-1">
          <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2">
            <Settings2 className="h-6 w-6" aria-hidden />
            Configuración de plataforma
          </h1>
          <p className="text-sm text-muted-foreground">
            Documento único en{" "}
            <code className="text-xs">viewmed_config</code> y catálogo de
            servicios con acceso. Solo admin / Admin IT.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          asChild
          className="shrink-0 cursor-pointer"
        >
          <Link to="/servers/report">Ver inventario</Link>
        </Button>
      </header>

      <Tabs
        value={activeTab}
        onValueChange={handleTabChange}
        className="w-full gap-4"
      >
        <div className="border-b border-border">
          <TabsList
            className="h-auto w-full justify-start gap-0 rounded-none bg-transparent p-0"
            aria-label="Secciones de configuración"
          >
            <TabsTrigger value="infra" className={TAB_TRIGGER_CLASS}>
              <Server className="size-4" aria-hidden />
              Infraestructura
            </TabsTrigger>
            <TabsTrigger value="platform" className={TAB_TRIGGER_CLASS}>
              <Bell className="size-4" aria-hidden />
              Plataforma
            </TabsTrigger>
            <TabsTrigger value="servicios" className={TAB_TRIGGER_CLASS}>
              <Boxes className="size-4" aria-hidden />
              Servicios
            </TabsTrigger>
            <TabsTrigger value="plantillas" className={TAB_TRIGGER_CLASS}>
              <FileText className="size-4" aria-hidden />
              Plantillas
            </TabsTrigger>
            <TabsTrigger value="planes" className={TAB_TRIGGER_CLASS}>
              <CreditCard className="size-4" aria-hidden />
              Planes
            </TabsTrigger>
            <Can permission="roles:view">
              <TabsTrigger value="roles" className={TAB_TRIGGER_CLASS}>
                <ShieldCheck className="size-4" aria-hidden />
                Roles
              </TabsTrigger>
            </Can>
            <Can permission="roles:view">
              <TabsTrigger value="equipo" className={TAB_TRIGGER_CLASS}>
                <Users className="size-4" aria-hidden />
                Equipo
              </TabsTrigger>
            </Can>
          </TabsList>
        </div>

        <TabsContent value="infra" className="mt-0 space-y-3 outline-none">
          <p className="text-xs text-muted-foreground">
            Intervalo de revisión, versiones objetivo y política de SO.
          </p>
          <RevisionIntervalEditor intervalMonths={intervalMonths} />
          <TargetVersionsEditor
            softwareTypes={softwareTypes}
            targetVersions={targetVersions}
          />
          <OsSupportPolicyEditor policy={osPolicy} />
        </TabsContent>

        <TabsContent value="platform" className="mt-0 space-y-3 outline-none">
          <p className="text-xs text-muted-foreground">
            Lista negra de emails y canales de notificación globales.
          </p>
          <BlacklistEmailEditor emails={blackListEmail} />
          <PlatformNotificationEditor notification={notification} />
        </TabsContent>

        <TabsContent value="servicios" className="mt-0 space-y-3 outline-none">
          <p className="text-xs text-muted-foreground">
            Servicios a los que se puede otorgar acceso (catálogo{" "}
            <code className="text-[11px]">api_permissions</code>).
          </p>
          <ApiEndpointsCatalogEditor />
        </TabsContent>

        <TabsContent value="plantillas" className="mt-0 space-y-3 outline-none">
          <p className="text-xs text-muted-foreground">
            Modelos de historia clínica y prompts de IA (
            <code className="text-[11px]">medical_templates</code>).
          </p>
          <MedicalTemplatesEditor />
        </TabsContent>

        <TabsContent value="planes" className="mt-0 space-y-3 outline-none">
          <p className="text-xs text-muted-foreground">
            Planes de suscripción, precios y vínculo con Stripe (
            <code className="text-[11px]">plans</code>).
          </p>
          <PlansEditor />
        </TabsContent>

        <TabsContent value="roles" className="mt-0 space-y-3 outline-none">
          <p className="text-xs text-muted-foreground">
            Roles y permisos del panel (
            <code className="text-[11px]">rbac_roles</code>). Editarlos cambia el acceso
            de inmediato, sin desplegar.
          </p>
          <Can
            permission="roles:view"
            fallback={
              <p className="text-sm text-muted-foreground">
                No tienes permiso para ver los roles.
              </p>
            }
          >
            <RolesEditor />
          </Can>
        </TabsContent>

        <TabsContent value="equipo" className="mt-0 space-y-3 outline-none">
          <p className="text-xs text-muted-foreground">
            Cuentas corporativas con acceso al panel (
            <code className="text-[11px]">staff</code>), separadas de los usuarios de la
            plataforma médica.
          </p>
          <Can
            permission="roles:view"
            fallback={
              <p className="text-sm text-muted-foreground">
                No tienes permiso para ver el equipo.
              </p>
            }
          >
            <StaffEditor />
          </Can>
        </TabsContent>
      </Tabs>
    </div>
  );
}
